import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Chip,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { conditionIdAtom, driveIdAtom, phenomenonIdAtom, stageConfigAtom } from "atoms/Rtool/stageConfigAtoms";
import { Dropdown } from "components/Dropdown";
import { ErrorState } from "components/ErrorState";
import { useIsAllNoiseError } from "utils/utils";
import { DisplayContribution } from "./viewer/DisplayContribution";
import { DRIVE_MODE_IDS, PHENOMENON_IDS } from "utils/consts";
import { DRIVE_MODE_IDS as HEV_DRIVE_MODE_IDS, PHENOMENON_IDS as HEV_PHENOMENON_IDS } from "utils/constsHev";
import { powertrainAtom } from "atoms/powertrainAtom";
import { DisplayNtf } from "./viewer/DisplayNtf";
import { DisplayMtgInsu } from "./viewer/DisplayMtgInsu";
import { DisplayMtgVibCtrTarget } from "./viewer/DisplayMtgVibCtrTarget";
import { DisplayMtgVibTarget } from "./viewer/DisplayMtgVibTarget";
import { DisplayCabinNoiseTarget } from "./viewer/DisplayCabinNoiseTarget";
import { useEachConditionDataSetter } from "../utils/useEachConditionDataSetter";
import { DownloadCsvButton } from "../download/DownloadEachConditionCSV";
import { ChartActions } from "components/charts/ChartActions";
import { useAtomValue, useSetAtom } from "jotai";
import { rCalcSetMtgAtom } from "atoms/Rtool/mtgAtoms";
import { HEV_SERIES_3MTG, SERIES_9, SERIES_12 } from "./viewer/seriesDefinitions";
import type { Series } from "utils/charts/types";
import { SeriesSelectionProvider } from "utils/SeriesSelectionProvider/SeriesSelectionContext";
import { mappingAtom } from "atoms/mappingAtoms";
import { buildLabeledOptions } from "utils/mappingLabel";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import { ChartLockProvider, type ChartLockValue } from "utils/charts/ChartLockContext";

const CHART_OPTIONS = [
    { id: "cabin_noise_target", label: "車室内音計算値", component: DisplayCabinNoiseTarget },
    { id: "contribution", label: "寄与率", component: DisplayContribution },
    { id: "ntf", label: "NTF", component: DisplayNtf },
    { id: "mtg_insu", label: "INSULATOR剛性", component: DisplayMtgInsu },
    { id: "mtg_vib_ctr_target", label: "INSULATOR中心振動", component: DisplayMtgVibCtrTarget },
    { id: "mtg_vib_target", label: "MTG先端振動", component: DisplayMtgVibTarget },
] as const;

export const EachConditionChart: React.FC = () => {
    const ph = useAtomValue(phenomenonIdAtom);
    const dr = useAtomValue(driveIdAtom);
    const powertrain = useAtomValue(powertrainAtom);
    const isHev = powertrain === 'hev';
    const mtg = useAtomValue(rCalcSetMtgAtom);
    const baseSeries = useMemo<Series[]>(
        () => {
            if (isHev && mtg === 3) return HEV_SERIES_3MTG;
            if (mtg === 4) return SERIES_12;
            return isHev ? SERIES_12 : SERIES_9;
        },
        [isHev, mtg]
    );
    const setCondition = useSetAtom(conditionIdAtom);
    const setStageConfig = useSetAtom(stageConfigAtom);
    const mapping = useAtomValue(mappingAtom);
    const phenomenonOptions = useMemo(
        () => buildLabeledOptions(mapping, 'phenomenon_id', isHev ? HEV_PHENOMENON_IDS : PHENOMENON_IDS),
        [mapping, isHev]
    );
    const driveModeOptions = useMemo(
        () => buildLabeledOptions(mapping, 'drive_mode_id', isHev ? HEV_DRIVE_MODE_IDS : DRIVE_MODE_IDS),
        [mapping, isHev]
    );

    useEffect(() => {
        if (ph && dr) {
            setCondition(`${ph}-${dr}`);
        } else {
            setStageConfig((prev) =>
                prev.condition_id === "" ? prev : { ...prev, condition_id: "" }
            );
        }
    }, [ph, dr, setCondition, setStageConfig]);

    useEachConditionDataSetter();
    const allError = useIsAllNoiseError();

    // B4: 個別グラフ表示で共有する x 軸固定状態（全グラフ共通の 1 状態）。
    const [lockedValue, setLockedValue] = useState<number | null>(null);
    const lastHoveredValueRef = useRef<number | null>(null);
    const toggleLock = useCallback(() => {
        // 固定中は解除、非固定なら直近ホバー位置で固定（位置はグラフ click でも指定可）。
        setLockedValue((prev) => (prev != null ? null : lastHoveredValueRef.current));
    }, []);
    // 固定中のみ Esc で解除する。
    useEffect(() => {
        if (lockedValue == null) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setLockedValue(null); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lockedValue]);
    const lockValue = useMemo<ChartLockValue>(
        () => ({ lockedValue, setLockedValue, lastHoveredValueRef }),
        [lockedValue],
    );

    const [selectedCharts, setSelectedCharts] = useState<string[]>(() =>
        CHART_OPTIONS.map((opt) => opt.id)
    );
    const [availableSeries, setAvailableSeries] = useState<Series[]>(baseSeries);
    const [selectedSeriesKeys, setSelectedSeriesKeys] = useState<string[]>(
        baseSeries.map((series) => series.key)
    );

    // mtg atom が遅れてセットされた場合も正しく同期する（beless E2E タイミング対策）
    useEffect(() => {
        setAvailableSeries(baseSeries);
        setSelectedSeriesKeys(baseSeries.map(s => s.key));
    }, [baseSeries]);

    const handleChartChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setSelectedCharts(typeof value === "string" ? value.split(",") : value);
    };

    const handleSeriesChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        setSelectedSeriesKeys(typeof value === "string" ? value.split(",") : value);
    };

    const registerAvailableSeries = useCallback(
        (seriesList: Series[]) => {
            setAvailableSeries((prev) => {
                const prevKeys = prev.map((s) => s.key).join("|");
                const nextKeys = seriesList.map((s) => s.key).join("|");
                if (prevKeys === nextKeys) return prev;
                setSelectedSeriesKeys((prevSelected) => {
                    const allowed = new Set(seriesList.map((s) => s.key));
                    const filtered = prevSelected.filter((key) => allowed.has(key));
                    return filtered.length ? filtered : seriesList.map((s) => s.key);
                });
                return seriesList;
            });
        },
        []
    );

    const seriesSelectionChips = useMemo(
        () =>
            selectedSeriesKeys.map((key) => {
                const series = availableSeries.find((option) => option.key === key);
                return { key, label: series?.name ?? key };
            }),
        [availableSeries, selectedSeriesKeys]
    );

    const seriesSelectOptions = useMemo(
        () => availableSeries.map((series) => ({ key: series.key, name: series.name })),
        [availableSeries]
    );

    const contextValue = useMemo(
        () => ({
            selectedSeriesKeys,
            setSelectedSeriesKeys,
            availableSeries,
            registerAvailableSeries,
        }),
        [availableSeries, registerAvailableSeries, selectedSeriesKeys]
    );

    return (
        <SeriesSelectionProvider value={contextValue}>
            <ChartLockProvider value={lockValue}>
            <Stack spacing={3}>
                {/* ドロップダウンは常に表示 */}
                <Box display="flex" gap={2} padding={2} alignItems="center">
                    <Dropdown stageAtom={phenomenonIdAtom} options={phenomenonOptions} label="現象" />
                    <Dropdown stageAtom={driveIdAtom} options={driveModeOptions} label="運転モード" />

                    {/* マルチセレクトチャート選択 */}
                    <FormControl sx={{ minWidth: 240 }}>
                        <InputLabel id="chart-select-label">表示グラフ</InputLabel>
                        <Select
                            labelId="chart-select-label"
                            multiple
                            value={selectedCharts}
                            onChange={handleChartChange}
                            input={<OutlinedInput label="表示グラフ" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {selected.map((id) => {
                                        const opt = CHART_OPTIONS.find((o) => o.id === id);
                                        return <Chip key={id} label={opt?.label} size="small" />;
                                    })}
                                </Box>
                            )}
                        >
                            {CHART_OPTIONS.map(({ id, label }) => (
                                <MenuItem key={id} value={id}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl sx={{ minWidth: 240 }}>
                        <InputLabel id="series-select-label">表示系列</InputLabel>
                        <Select
                            labelId="series-select-label"
                            multiple
                            value={selectedSeriesKeys}
                            onChange={handleSeriesChange}
                            input={<OutlinedInput label="表示系列" />}
                            renderValue={() =>
                                seriesSelectionChips.length ? (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                        {seriesSelectionChips.map(({ key, label }) => (
                                            <Chip key={key} label={label} size="small" />
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="caption" color="text.disabled">
                                        選択なし
                                    </Typography>
                                )
                            }
                        >
                            {seriesSelectOptions.map(({ key, name }) => (
                                <MenuItem key={key} value={key}>
                                    {name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title={lockedValue != null ? "x軸の固定を解除" : "x軸を固定（グラフをクリックで位置指定 / Esc で解除）"}>
                        <IconButton
                            onClick={toggleLock}
                            color={lockedValue != null ? "primary" : "default"}
                            aria-label={lockedValue != null ? "x軸の固定を解除" : "x軸を固定"}
                        >
                            {lockedValue != null ? <PushPinIcon /> : <PushPinOutlinedIcon />}
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* エラー時は画像＋メッセージ、正常時はグラフ */}
                {allError ? (
                    <ErrorState
                        message="指定された条件に合致するデータがありません。"
                        actions={[{ label: "計算セットを選択", to: "/r-tools/existing-project" }]}
                    />
                ) : (
                    CHART_OPTIONS.map(({ id, label, component: Comp }) =>
                        selectedCharts.includes(id) ? (
                            <Box key={id} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                <ChartActions filename={label} title={label}>
                                    <Comp title={label} graph_id={id as any} />
                                </ChartActions>
                                <Box sx={{ alignSelf: "flex-end" }}>
                                    <DownloadCsvButton datasetKey={id as any} />
                                </Box>
                            </Box>
                        ) : null
                    )
                )}
            </Stack>
            </ChartLockProvider>
        </SeriesSelectionProvider>
    );
};
